/**
 * ExistingSeatsPanel — vacant positions that already match an ask.
 *
 * Shown before HR requests a new position (NewPositionDialog), on each open
 * request HR follows (HR Ops → Position requests), and to the reviewer (Role
 * Master → Requests). A new position is the last resort: a vacant one of the
 * same job, type and dimensions is used instead whenever it can be.
 *
 * A lookup returns only the closest few of each kind, while its counts are the
 * real totals. The panel counts from the totals and, when someone asks for
 * more than it was given, asks again for that one request's full list.
 */
import React, { useState } from 'react';
import { positionRequestService } from '../api/services/positionRequestService';
import type { ExistingSeat, ExistingSeatsResult } from '../api/services/positionRequestService';
import {
  dimsLabel, excludedNote, matchHeadline, seatFacts, splitCandidates,
} from './positionRequestStatus';

const FACT_TONE: Record<string, string> = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  warn: 'border-amber-300 bg-amber-50 text-amber-900',
  neutral: 'border-gray-200 bg-gray-50 text-gray-600',
};

interface Props {
  result: ExistingSeatsResult | null;
  loading?: boolean;
  /** Shows a button per position when set. */
  onUse?: (seat: ExistingSeat) => void;
  useLabel?: string;
  /** Render nothing (rather than "none match") when there are no matches. */
  quietWhenEmpty?: boolean;
  /** How many close matches to show before "Show more". */
  initiallyShown?: number;
}

const SeatRow: React.FC<{ seat: ExistingSeat; onUse?: (s: ExistingSeat) => void; useLabel: string }> = ({
  seat, onUse, useLabel,
}) => (
  <li className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2">
    <div className="min-w-0">
      <div className="text-sm text-gray-900">
        <span className="font-mono font-semibold">{seat.fo_position_id}</span>
        {' '}{seat.title}
        <span className="text-gray-500"> · {dimsLabel(seat) || 'no dimensions'}</span>
        {seat.position_type_id && <span className="text-gray-400"> · {seat.position_type_id}</span>}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {seatFacts(seat).map((f) => (
          <span key={f.text} title={f.title}
            className={`rounded-full border px-2 py-0.5 text-[11px] ${FACT_TONE[f.tone]}`}>
            {f.text}
          </span>
        ))}
      </div>
    </div>
    {onUse && (
      <button type="button" onClick={() => onUse(seat)}
        className="shrink-0 rounded-md border border-gray-900 px-2.5 py-1 text-xs font-medium text-gray-900 hover:bg-gray-900 hover:text-white">
        {useLabel}
      </button>
    )}
  </li>
);

// Says how many of the total are listed when not all of them are, and offers
// to try again when the full list didn't load.
const PartialNote: React.FC<{ shown: number; total: number; failed: boolean; onRetry?: () => void }> = ({
  shown, total, failed, onRetry,
}) => (
  <p className="mt-1 text-[11px] text-gray-500">
    {shown ? `Showing the ${shown} closest of ${total}.` : `These ${total} aren't listed here.`}
    {failed && onRetry && (
      <>
        {' '}The rest didn't load.{' '}
        <button type="button" className="font-medium underline" onClick={onRetry}>Try again</button>
      </>
    )}
  </p>
);

const ExistingSeatsPanel: React.FC<Props> = ({
  result, loading, onUse, useLabel = 'Use this position', quietWhenEmpty, initiallyShown = 4,
}) => {
  const [showAllClose, setShowAllClose] = useState(false);
  const [showElsewhere, setShowElsewhere] = useState(false);
  // The full list belongs to the result it was asked for; a new result drops it.
  const [full, setFull] = useState<{ of: ExistingSeatsResult; data: ExistingSeatsResult } | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [fullFailed, setFullFailed] = useState(false);

  if (loading && !result) {
    return <p className="text-xs text-gray-500">Checking for vacant positions of this role…</p>;
  }
  if (!result) return null;
  const fullData = full && full.of === result ? full.data : null;
  const { close, elsewhere } = splitCandidates(fullData || result);
  const closeTotal = result.counts.exact + result.counts.near;
  const elsewhereTotal = result.counts.elsewhere;
  const asked = result.asked;
  const canLoadFull = !fullData && !!asked && !!(asked.job_id || asked.title);
  const note = excludedNote(result);
  if (!closeTotal && !elsewhereTotal) {
    if (quietWhenEmpty) return null;
    return (
      <p className="text-xs text-gray-500">
        {matchHeadline(result)}.{note ? ` ${note}` : ''}
      </p>
    );
  }

  const loadFull = async () => {
    if (!asked || loadingFull) return;
    setLoadingFull(true);
    setFullFailed(false);
    try {
      const res = await positionRequestService.existingSeats([{
        key: 'full',
        full: true,
        job_id: asked.job_id,
        title: asked.title,
        position_type_id: asked.position_type_id,
        campus: asked.campus,
        department: asked.department,
        faculty: asked.faculty,
        reports_to_position_id: asked.reports_to,
        start_date: asked.year ? `${asked.year}-01-01` : null,
        exclude: asked.exclude || [],
      }]);
      const data = res.results?.full;
      if (!data) throw new Error('No result for the full list');
      setFull({ of: result, data });
    } catch (e) {
      console.error('Full list of vacant positions failed', e);
      setFullFailed(true);
    } finally {
      setLoadingFull(false);
    }
  };
  const showMoreClose = () => {
    setShowAllClose(true);
    if (close.length < closeTotal && canLoadFull) void loadFull();
  };
  const toggleElsewhere = () => {
    const opening = !showElsewhere;
    setShowElsewhere(opening);
    if (opening && elsewhere.length < elsewhereTotal && canLoadFull) void loadFull();
  };

  const shownClose = showAllClose ? close : close.slice(0, initiallyShown);
  const moreClose = closeTotal - shownClose.length;
  return (
    <div className={`rounded-md border px-3 py-2 ${closeTotal
      ? 'border-amber-300 bg-amber-50/70' : 'border-gray-200 bg-gray-50'}`}>
      <div className={`text-sm font-semibold ${closeTotal ? 'text-amber-900' : 'text-gray-700'}`}>
        {matchHeadline(result)}
      </div>
      {closeTotal > 0 && (
        <p className="mt-0.5 text-xs text-amber-900">
          Use one of these instead of a new position wherever you can: a new position needs its own
          budget, and the vacant one stays empty.
        </p>
      )}
      {shownClose.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {shownClose.map((s) => <SeatRow key={s.fo_position_id} seat={s} onUse={onUse} useLabel={useLabel} />)}
        </ul>
      )}
      {!showAllClose && moreClose > 0 && (
        <button type="button" className="mt-1.5 text-xs font-medium text-amber-900 underline"
          onClick={showMoreClose}>
          Show {moreClose} more
        </button>
      )}
      {showAllClose && close.length < closeTotal && (loadingFull
        ? <p className="mt-1 text-[11px] text-gray-500">Loading all {closeTotal}…</p>
        : <PartialNote shown={close.length} total={closeTotal} failed={fullFailed}
            onRetry={canLoadFull ? loadFull : undefined} />)}
      {elsewhereTotal > 0 && (
        <div className="mt-2">
          <button type="button" className="text-xs font-medium text-gray-700 underline"
            onClick={toggleElsewhere}>
            {showElsewhere ? 'Hide' : 'Show'} {elsewhereTotal} at another campus or department
          </button>
          {showElsewhere && elsewhere.length > 0 && (
            <ul className="mt-1.5 space-y-1.5">
              {elsewhere.map((s) => <SeatRow key={s.fo_position_id} seat={s} onUse={onUse} useLabel={useLabel} />)}
            </ul>
          )}
          {showElsewhere && elsewhere.length < elsewhereTotal && (loadingFull
            ? <p className="mt-1 text-[11px] text-gray-500">Loading all {elsewhereTotal}…</p>
            : <PartialNote shown={elsewhere.length} total={elsewhereTotal} failed={fullFailed}
                onRetry={canLoadFull ? loadFull : undefined} />)}
        </div>
      )}
      {note && <p className="mt-2 text-[11px] text-gray-500">{note}</p>}
    </div>
  );
};

export default ExistingSeatsPanel;
